import * as React from 'react';
import withStyles from 'react-jss';
import { tokens } from 'src/design-system';
import { StylesProps } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { Modal, Icon, Button, message, Row, Col, Spin } from 'src/Library/AntdLibComponents';
import { commonStyles, buttonStyle } from 'src/Library/CommonStyles';
import { createAddress } from 'src/api/organisationProfiling';
import { isValidArray } from 'src/Library/Helper';
import { QuickAddContainerTrackingStyles } from './QuickAddContainerTracking.styles';
import Form, { FormComponentProps } from 'antd/lib/form';
import isEmpty from 'lodash/isEmpty';
import { getFormattedAddress } from '../Triangulation/ContainerTriangulation.utils';
import AddEditAddressForm from '../Triangulation/AddEditAddressForm';
import { SEConsignorAddress } from '../shipment-execution/SE.interfaces';

const selectImportAddressModalStyles = (theme) => ({
  modal: {
    minWidth: '835px',
  },
  justifySpaceBetween: {
    ...commonStyles(theme).justifySpaceBetween,
  },
  addAddress: {
    fontSize: tokens.fontSize.small,
    color: tokens.colors.primary,
    cursor: 'pointer',
  },
  doneButton: {
    ...buttonStyle(tokens.colors.bgWhite, tokens.colors.primaryDark, `1px solid ${tokens.colors.primaryDark}`),
    borderRadius: tokens.radius.md,
    padding: `0 ${tokens.spacing.md}px`,
  },
});

const styles = (theme) => ({
  ...QuickAddContainerTrackingStyles(theme),
  ...selectImportAddressModalStyles(theme),
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  handleClose: () => void;
  isVisible: boolean;
  handleDone: (address?: SEConsignorAddress) => void;
  selectedImportAddress?: SEConsignorAddress;
  addressList: SEConsignorAddress[];
  fetchAddressList: () => void;
  isFetchingAddress?: boolean;
}

interface StateProps {
  selectedAddress?: SEConsignorAddress;
  isAddingNewAddressOnMap?: boolean;
  destuffingLocation?;
  isAddingNewAddressOnForm?: boolean;
  isAddingAddress?: boolean;
}

const SelectImportAddressModal = (props: IProps): React.ReactElement => {
  const {
    classes,
    form,
    handleClose,
    isVisible,
    handleDone,
    selectedImportAddress,
    addressList,
    fetchAddressList,
    isFetchingAddress,
  } = props;
  
  const [state, setState] = useGenericState<StateProps>({
    selectedAddress: selectedImportAddress,
  });

  const {
    selectedAddress,
    isAddingNewAddressOnMap,
    destuffingLocation,
    isAddingNewAddressOnForm,
    isAddingAddress,
  } = state;

  React.useEffect(() => {
    if (!isValidArray(addressList)) {
      fetchAddressList();
    }
  }, []);

  const renderTitle = () : React.ReactElement => {
    if (isAddingNewAddressOnMap) {
      return (
        <span style={{ fontWeight: 600 }}>
          <Icon
            type="close"
            onClick={() => setState({ isAddingNewAddressOnMap: false })}
            style={{ marginRight: '8px' }}
          /> Add New Import Address
        </span>
      );
    };
    if (isAddingNewAddressOnForm) {
      return (
        <span style={{ fontWeight: 600 }}>
          <Icon
            type="arrow-left"
            onClick={() => setState({ isAddingNewAddressOnMap: true, isAddingNewAddressOnForm: false })}
            style={{ marginRight: '8px' }}
          /> Add New Import Address
        </span>
      );
    };
    return (
      <div className={classes.justifySpaceBetween}>
        <span style={{ fontWeight: 600 }}>
          <Icon type="close" onClick={handleClose} style={{ marginRight: '8px' }} /> Select Import Address
        </span>
        {isValidArray(addressList) &&
          <span
            className={classes.addAddress}
            onClick={() => setState({ isAddingNewAddressOnMap: true })}
          >
            + Add New Address
          </span>
        }
      </div>
    );
  };

  const renderAddress = () : React.ReactElement => {
    // dividing addressList in three columns per row
    const rows = [...Array(Math.ceil(addressList.length / 3))];
    const addressRows = rows.map((row, ind) => addressList.slice(ind * 3, ind * 3 + 3) );
    const content = addressRows.map((row, ind) => (
      <Row key={ind} style={{ display: 'flex' }}>    
        {row.map(address => (
          <Col span={8} key={address.id} style={{ padding: '4px' }}>
            <div
              className={classes.selectedAddress}
              style={{ border: `1px solid ${address.id === selectedAddress?.id ? tokens.colors.primaryDark : tokens.colors.border }` }}
              onClick={() => setState({ selectedAddress: address })}
            >
              <div className={classes.addressHeading}>{address.name}</div>
              <div className={classes.addressOtherDetails}>{getFormattedAddress(address)}</div>
            </div>
          </Col>
        ))}
      </Row>)
    );
    return (
      <div>
        {content}
      </div>
    );
  };

  const renderEmptyState = () : React.ReactElement => {
    return (
      <div style={{ fontSize: '18px', textAlign: 'center', padding: '30px 0' }}>
        <div style={{ fontWeight: tokens.fontWeight.semibold, color: tokens.colors.textSecondary, marginBottom: '10px' }}>There are no addresses saved.</div>
        <div
          className={classes.addAddress}
          style={{ fontSize: '18px' }}
          onClick={() => setState({ isAddingNewAddressOnMap: true })}
        >
          + Add New Address
        </div>
      </div>
    );
  };

  const renderData = () : React.ReactElement => {
    if (isAddingNewAddressOnMap) {
      return renderAddAddress('googleMap');
    }
    if (isAddingNewAddressOnForm) {
      return renderAddAddress('form');
    }
    if (!isValidArray(addressList)) {
      return renderEmptyState();
    }
    return renderAddress();
  };

  const setLocationFields = (props) : void => {
    const locationType = form.getFieldValue('destuffingLocation.locationType');
    setState({ destuffingLocation: {
      ...destuffingLocation,
      locationType,
      ...props?.destuffingLocation,
    }});
  }

  const renderAddAddress = (formType) : React.ReactElement => {
    return (
      <React.Fragment>
        <AddEditAddressForm
          isAddingAddressOnMap={formType === 'googleMap'}
          isAddingAddressForm={formType === 'form'}
          form={form}
          addressType="destuffingLocation"
          setLocationFields={setLocationFields}
          prefillingAddress={destuffingLocation?.mapLocation}
        />
        <div style={{ marginBottom: '36px' }} />
      </React.Fragment>
    );
  };

  const handleOk = () : void => {
    if (isAddingNewAddressOnMap) {
      if (isEmpty(destuffingLocation?.mapLocation)) {
        message.error('Please select map location');
        return;
      }
      setState({
        isAddingNewAddressOnMap: false,
        isAddingNewAddressOnForm: true,
      });
    } else if (isAddingNewAddressOnForm) {
      form.validateFieldsAndScroll(async (err, formValues) => {
        if (err) {
          return;
        }
        setState({ isAddingAddress: true });
        const body = {
          ...formValues?.destuffingLocation,
          locationType: destuffingLocation?.locationType,
          location: 'FACTORY',
          addressTypes: ['DESTUFFING'],
          countryId: formValues?.countryId?.key === destuffingLocation?.mapLocation?.country ?
            undefined : formValues?.countryId?.key, /* country name will be sent if the country is prefilled from the map
            since we will not get the country id from googlemap */
          countryName: formValues?.countryId?.key === destuffingLocation?.mapLocation?.country ?
            formValues?.countryId?.key : undefined,
          latitude: destuffingLocation?.mapLocation?.latLng?.lat,
          longitude: destuffingLocation?.mapLocation?.latLng?.lng,
        };
        const response = await createAddress(body);
        if (response.isSuccess) {
          message.success('Address added successfully');
          setState({ isAddingAddress: false, isAddingNewAddressOnForm: false, isAddingNewAddressOnMap: false });
          fetchAddressList();
        } else {
          message.error(response.errorMessage);
          setState({ isAddingAddress: false });
        }
      });
    } else if (!selectedAddress) {
      message.error('Select address!');
    } else {
      handleDone(selectedAddress);
    }
  };

  return (
    <Modal
      visible={isVisible}
      className={classes.modal}
      title={renderTitle()}
      closable={false}
      onCancel={handleClose}
      footer={
        <Button
          onClick={() => handleOk()}
          className={classes.doneButton}
          loading={isAddingAddress}
        >
          {isAddingNewAddressOnMap ? 'Next' : 'Done'}
        </Button>
      }
    >
      {isFetchingAddress ? <Spin size="large" style={{ margin: '10% 48%' }} /> : renderData()}
    </Modal>
  );
};

export default React.memo(withStyles(styles)(Form.create()(SelectImportAddressModal)));